import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import api from 'services';
import { LoginPayload, LoginResponse } from 'services/requests';
import { ACCOUNT_BLOCKED_API_ERRORS, ApiError } from 'services/api.errors';
import { MutationState } from 'react-query/types/core/mutation';
import { useNavigate } from 'hooks/routes';
import { MutationKeys } from 'constants/mutationKeys';

export const useLogin = () => {
  const { toLoginBlocked } = useNavigate();

  const mutate = async (credentials: LoginPayload) => {
    const { data } = await api.login(credentials);
    return data;
  };

  const mutationKey = MutationKeys.login;

  return useMutation<LoginResponse, AxiosError<ApiError>, LoginPayload>(
    mutationKey,
    mutate,
    {
      onSuccess: async ({ tokenData }) => {
        api.setTokens(tokenData?.accessToken, tokenData?.refreshToken, false);
      },
      onError(error, variables, context) {
        if (
          error.response?.data?.code &&
          Object.values(ACCOUNT_BLOCKED_API_ERRORS).includes(error.response?.data.code)
        ) {
          toLoginBlocked(error.response?.data);
        }
      },
    },
  );
};

export const useStateLogin = () => {
  const queryClient = useQueryClient();
  const mutationCache = queryClient.getMutationCache();
  const initialData = mutationCache.find<
    LoginResponse,
    AxiosError<ApiError>,
    void,
    unknown
  >({ mutationKey: MutationKeys.login });
  const [state, setState] = useState(initialData?.state);

  useEffect(() => {
    const unsubscribe = mutationCache.subscribe((mutation) => {
      if (mutation?.options.mutationKey === MutationKeys.login)
        setState(
          mutation.state as
            | MutationState<LoginResponse, AxiosError<ApiError, any>, void, unknown>
            | undefined,
        );
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return state;
};
